import { getAbsoluteUrl } from "$utils/urls.js";
import { csv } from "d3";
import { setContext, getContext } from "svelte";
import { GROUPING_OPTIONS, SEGREGATION_OPTIONS } from "./constants.js";

let data_file = "district_dataset_6_22_final.csv";

export class ToolData {
  allData = $state();
  groupedData = $derived(this.groupData(this.allData));

  activeSegregationMetric = $state("NormalizedExpWhiteMinority");
  activeSegregationMetricLabel = $derived(
    SEGREGATION_OPTIONS.find((d) => d.value == this.activeSegregationMetric)?.label
  );
  activeRightChartGrouping = $state(null);
  activeRightChartGroupingLabel = $derived(
    GROUPING_OPTIONS.find((d) => d.value == this.activeRightChartGrouping)?.label
  );
  activeGroupingNames = $derived(
    this.activeRightChartGrouping
      ? GROUPING_OPTIONS.find((d) => d.value == this.activeRightChartGrouping)
          ?.breakdown //.sort((a, b) => a.value - b.value)
          .map((d) => d.label)
      : []
  );
  // Breakdown values in the same order as activeGroupingNames. Used to pin the
  // beeswarm color scale's domain so dot colors match the legend (which colors
  // by breakdown order); otherwise LayerCake derives the domain from data order
  // and the two drift apart. Coerced to strings so the domain matches the raw
  // CSV values used by the z accessor (d3 scaleOrdinal does not intern number
  // 3 and string "3" as the same key).
  activeGroupingValues = $derived(
    this.activeRightChartGrouping
      ? GROUPING_OPTIONS.find((d) => d.value == this.activeRightChartGrouping)?.breakdown.map(
          (d) => String(d.value)
        )
      : []
  );
  activeDistrictID = $state("622710");
  activeDistrictData = $derived(
    this.activeDistrictID && this.isReady() ? this.groupedData[this.activeDistrictID] : null
  );

  isGlossaryOpen = $state(false);

  leftScatterPlotData = $derived(this.getScatterData());
  rightBeesSortedData = $derived(this.sortBeesData());

  districtDropdownData = $derived(
    this.isReady()
      ? this.allData
          .filter((d) => d.metric == "DistrictName")
          .map((d) => ({ label: d.value, value: d.NCESID }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : []
  );

  // logic to handle the toggle in the left side scatterplot
  highlightSameState = $state(false); // toggle will update this decide whether or not to show highlights
  
  hasSameStateDistricts = $derived(
    this.activeDistrictData 
      ? Object.values(this.groupedData).some((d) => d["State"] === this.activeDistrictData["State"] && d["DistrictID"] !== this.activeDistrictData["DistrictID"])
      : false
  )

  sameStateDistrictIDs = $derived(
    this.highlightSameState && this.hasSameStateDistricts
    ? new Set(
      Object.values(this.groupedData)
        .filter((d) => d.State === this.activeDistrictData.State &&
          d.DistrictID !== this.activeDistrictData.DistrictID
        ).map((d) => d.DistrictID)
    )
    : new Set()
  )


  //DATA
  //This grabs our data from file
  async fetchData() {
    this.allData = await csv(getAbsoluteUrl(data_file));
  }

  //here we group our data by school for use in the frontend by metric
  groupData(arr) {
    let numeric_vars = ["BoundaryPolicyRobustness05", ...SEGREGATION_OPTIONS.map((d) => d.value)];

    if (!arr) return null;
    return arr.reduce((acc, curr) => {
      if (!acc[curr.NCESID]) {
        acc[curr.NCESID] = {};
        acc[curr.NCESID]["DistrictID"] = curr.NCESID;
      }
      if (numeric_vars.includes(curr.metric)) {
        acc[curr.NCESID][curr.metric] = +curr.value;
      } else {
        acc[curr.NCESID][curr.metric] = curr.value;
      }
      return acc;
    }, {});
  }

  //is our data loaded and grouped? use to allow rendering of certain frontend elements and
  //certain data transformations
  isReady() {
    return this.allData && this.groupedData && Object.keys(this.groupedData).length > 0;
  }

  //if data is loaded, parse it down to just our possible x values (our segregation metrics)
  //and our one y value (bondary robustness). then we can just change which x variable to activate
  //directly from teh dropdown metric held in this file.
  //this needs to happen only once, on isReady()
  getScatterData() {
    if (!this.isReady()) return [];
    let segregation_vars = SEGREGATION_OPTIONS.map((d) => d.value);
    let string_vars = ["DistrictName", "State", "DistrictID"];
    let number_vars = ["BoundaryPolicyRobustness05", ...segregation_vars];
    let all_vars = [...string_vars, ...number_vars];

    return Object.values(this.groupedData).map((d) => {
      let obj = {};
      for (let key of all_vars) {
        if (string_vars.includes(key)) obj[key] = d[key];
        else obj[key] = +d[key];
      }
      return obj;
    });
  }

  sortBeesData() {
    if (!this.isReady() || !this.activeDistrictID) {
      return { match: [], nonMatch: [] };
    }

    const selected = this.activeDistrictData;

    let policy_vars = [
      "HasCharterSchools",
      "HasMagnetSchools",
      "HasSpecializedSchools",
      "TransporttoChoice",
      "StatePolicyInter",
      "StatePolicyIntra"
    ];

    return Object.values(this.groupedData).reduce(
      (acc, row) => {
        const samePolicy = policy_vars.every((key) => row[key] === selected[key]);
        if (samePolicy) acc.match.push(row);
        else acc.nonMatch.push(row);
        return acc;
      },
      { match: [], nonMatch: [] }
    );
  }

  getGroupingValueLabelFromValue(value) {
    if (!value) return null;
    if (!this.activeRightChartGrouping) return null;
    return GROUPING_OPTIONS.find((d) => d.value === this.activeRightChartGrouping).breakdown.find(
      (d) => d.value == +value
    ).label;
  }
}

let contextKey = "TOOL_DATA";

export function createData() {
  const data = new ToolData();
  setContext(contextKey, data);
  return data;
}

export function useData() {
  return getContext(contextKey);
}
